import React from 'react';
import { CaretSortIcon, CheckIcon, Cross2Icon } from '@radix-ui/react-icons';
import { ApiErrorAlert } from 'components/ui/api-error-alert';
import { Badge } from 'components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { LoadingMessage } from 'components/Loader/LoadingMessage';
import { useService } from 'hooks/useService';
import { useToast } from 'hooks/useToast';
import { ApiData, ApiDocument, ApiError } from 'interfaces/api';
import { Tag } from 'interfaces/tag';
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { ContentType } from 'services/abstract';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';

interface TagsSelectorProps {
  onChange: (tags: number[]) => void;
  selected?: number[];
}

export const TagsSelector: React.FC<TagsSelectorProps> = React.forwardRef<
  HTMLDivElement,
  TagsSelectorProps
>(({ onChange, selected = [] }, ref) => {
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(selected);
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { addToast } = useToast();
  const tagService = useService(ContentType.TAGS);

  const {
    data: response,
    isError,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => await tagService.getAllTags(),
  });

  const createTagMutation = useMutation<ApiDocument<Tag>, ApiError, Tag>({
    mutationFn: async (newTag: Tag): Promise<ApiDocument<Tag>> => {
      return await tagService.createTag(newTag);
    },
    onSuccess: async ({ data }: ApiDocument<Tag>) => {
      addToast({
        title: 'Nouveau tag créé',
        description: 'Votre Tag a été ajouté avec succès',
        type: 'success',
      });
      await refetch();
      handleTagSelection(data);
      onChange([...selectedTagIds, data.id]);
    },
  });

  const tags = useMemo(() => response?.data || [], [response]);
  const selectedTags = useMemo(() => {
    return selectedTagIds.map((id) => {
      return tags.find((tag) => id === tag.id) as ApiData<Tag>;
    });
  }, [tags, selectedTagIds]);

  const availableTags = useMemo(() => {
    return tags.filter(({ id }) => !selectedTagIds.includes(id));
  }, [tags, selectedTagIds]);

  const normalize = (value: string) => value.toLowerCase().replace(/\s+/g, '');

  const filteredTags = useMemo(() => {
    if (searchValue.trim() === '') {
      return availableTags;
    }
    const normalizedQuery = normalize(searchValue);
    return availableTags.filter((tag) =>
      normalize(tag.name).includes(normalizedQuery)
    );
  }, [availableTags, searchValue]);

  if (isLoading) {
    return <LoadingMessage data-testid="tags-loading-message" />;
  }

  if (isError) {
    return (
      <ApiErrorAlert error={error as ApiError} data-testid="error-message" />
    );
  }

  const handleTagSelection = (tag: ApiData<Tag>) => {
    const isSelected = selectedTagIds.includes(tag.id);
    if (!isSelected) {
      const selectedIds = [...selectedTagIds, tag.id];
      setSelectedTagIds(selectedIds);
      onChange(selectedIds);
    }
  };

  const handleTagDeselect = (tag: ApiData<Tag>) => {
    const updatedTags = selectedTags.filter(
      (selectedTag) => selectedTag.id !== tag.id
    );
    setSelectedTagIds(updatedTags.map((tag) => tag.id));
    onChange(updatedTags.map((tag) => tag.id));
  };

  const handleSubmit = (name: string) => {
    const alreadyExist = tags.find((t) => t.name === name);
    if (!alreadyExist) {
      createTagMutation.mutateAsync({ name });
    }
  };

  const handleComboboxSelect = (tag: ApiData<Tag>) => {
    handleTagSelection(tag);
    setIsComboboxOpen(false);
    setSearchValue('');
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (event.key === 'Enter' || event.key === 'NumpadEnter') &&
      searchValue.trim() &&
      filteredTags.length === 0
    ) {
      event.preventDefault();
      handleSubmit(searchValue.trim());
      setIsComboboxOpen(false);
      setSearchValue('');
    }
  };

  const handleOpenChange = (value: boolean) => {
    setIsComboboxOpen(value);
    if (!value) {
      setSearchValue('');
    }
  };

  return (
    <div className="w-3/4" ref={ref}>
      <div className="flex flex-wrap">
        {selectedTags.map((tag) => (
          <Badge
            key={tag.id}
            className="my-1 mr-2 inline-flex items-center gap-2"
            data-testid="selected-tags"
          >
            {tag.name}
            <Cross2Icon
              onClick={() => handleTagDeselect(tag)}
              className="inline-block h-4 w-4 cursor-pointer"
            />
          </Badge>
        ))}
        <Popover open={isComboboxOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={isComboboxOpen}
              className="my-1 flex h-9 items-center gap-2 rounded-md border border-dashed border-gray-300 bg-white px-3 text-sm text-gray-500 shadow-sm hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              data-testid="combobox-trigger"
            >
              Ajouter des tags ....
              <CaretSortIcon
                className="h-4 w-4 opacity-50"
                aria-hidden="true"
              />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0">
            <Command>
              <CommandInput
                placeholder="Ajouter des tags ...."
                value={searchValue}
                onValueChange={setSearchValue}
                onKeyDown={handleInputKeyDown}
                data-testid="input"
                autoFocus
              />
              <CommandList>
                <CommandEmpty>
                  {searchValue.trim()
                    ? 'Aucun tag trouvé! Tapez "Entrée" pour ajouter.'
                    : 'Aucun tag disponible.'}
                </CommandEmpty>
                <CommandGroup>
                  {filteredTags.map((tag) => (
                    <CommandItem
                      key={tag.id}
                      value={tag.name}
                      onSelect={() => handleComboboxSelect(tag)}
                    >
                      <CheckIcon
                        className="mr-2 h-4 w-4 text-blue-600 opacity-0 data-[selected]:opacity-100"
                        aria-hidden="true"
                      />
                      {tag.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
});
